/**
 * „Wyślij prośbę w czacie" as a state machine: the ask card, then the note after it.
 *
 * Each write carries a `clientId` the chat deduplicates by, and those ids are minted once per
 * request and kept across a retry. A retry after a lost answer therefore cannot post the card a
 * second time, and a retry after the card went through and the note did not sends only the note.
 */

export type RecordingRequestStatus = 'idle' | 'sending' | 'sent' | 'noEmail' | 'failed';
export type RecordingRequestStep = 'card' | 'note';

export interface RecordingRequestState {
  readonly status: RecordingRequestStatus;
  readonly cardId: string | null;
  readonly noteId: string | null;
  readonly cardSent: boolean;
}

export const NO_REQUEST: RecordingRequestState = {
  status: 'idle',
  cardId: null,
  noteId: null,
  cardSent: false,
};

/** The trainer pressed send. `null` while a send is in flight or once the request went through. */
export function beginRequest(
  state: RecordingRequestState,
  mintId: () => string,
): {
  readonly state: RecordingRequestState;
  readonly steps: readonly RecordingRequestStep[];
  /** The `clientId` each step is written under. */
  readonly ids: Readonly<Record<RecordingRequestStep, string>>;
} | null {
  if (state.status === 'sending' || state.status === 'sent') return null;
  const ids = { card: state.cardId ?? mintId(), note: state.noteId ?? mintId() };
  return {
    state: { ...state, status: 'sending', cardId: ids.card, noteId: ids.note },
    steps: state.cardSent ? ['note'] : ['card', 'note'],
    ids,
  };
}

export function stepSent(
  state: RecordingRequestState,
  step: RecordingRequestStep,
): RecordingRequestState {
  return step === 'card' ? { ...state, cardSent: true } : { ...state, status: 'sent' };
}

export function requestFailed(state: RecordingRequestState): RecordingRequestState {
  return { ...state, status: 'failed' };
}

/** The client has no email, so there is no conversation to write into. Nothing was sent. */
export function requestHasNoConversation(state: RecordingRequestState): RecordingRequestState {
  return { ...state, status: 'noEmail' };
}
